import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export const money = (v) => `$${Number(v || 0).toLocaleString("en-AU", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
export const price = money;
export const FEATURE_LABELS = { time_clock: "Time clock & breaks", timesheets: "Timesheets", leave: "Leave requests", announcements: "Announcements", fixed_wage: "Fixed-wage settings", basic_reports: "Basic wage reports",
  documents: "Staff documents", payroll: "Payroll report", rosters: "Staff rostering", geofencing: "GPS geofencing", advanced_reports: "Advanced wage reports", expiry_alerts: "Licence expiry alerts",
  gps_breadcrumbs: "Live GPS breadcrumbs", analytics: "Advanced analytics", priority_support: "Priority support" };

export const planPrice = (p, interval) => (interval === "year" ? p.base_monthly * 12 * (1 - p.annual_discount) : p.base_monthly);

export const IntervalToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="interval-toggle">
    {[["month", "Monthly"], ["year", "Annual · save 15%"]].map(([k, l]) => <button key={k} onClick={() => onChange(k)} data-testid={`interval-${k}`} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${value === k ? "bg-white text-[#0B2D5B] shadow-sm" : "text-slate-600"}`}>{l}</button>)}
  </div>
);

export const PlanCards = ({ plans, interval, action, current }) => (
  <div className="grid gap-5 md:grid-cols-3">
    {plans.map((p) => (
      <div key={p.plan_id} className={`relative flex flex-col rounded-3xl border bg-white p-6 transition-transform hover:-translate-y-1 ${p.popular ? "border-[#00C16B] shadow-xl ring-2 ring-[#00C16B]" : "border-slate-200"}`} data-testid={`plan-card-${p.plan_id}`}>
        {p.popular && <span className="absolute -top-3 left-6 flex items-center gap-1 rounded-full bg-[#FF8A00] px-3 py-1 text-xs font-bold text-white"><Star className="h-3 w-3" />Most popular</span>}
        <p className="text-lg font-bold text-[#0B2D5B]">{p.name}</p>
        <p className="mt-3"><span className="font-display text-4xl font-bold" data-testid={`plan-price-${p.plan_id}`}>{money(Math.round(planPrice(p, interval) * 100) / 100)}</span><span className="text-sm text-slate-500"> AUD / {interval === "year" ? "year" : "month"} + GST</span></p>
        {interval === "year" && <p className="text-xs text-[#00A85C]">{money(Math.round(planPrice(p, "year") / 12 * 100) / 100)}/month billed annually</p>}
        <ul className="mt-4 space-y-1.5 text-sm">
          <li className="font-semibold">{p.included_employees} employees included</li>
          <li className="font-semibold">{p.included_locations} location{p.included_locations === 1 ? "" : "s"}</li>
          <li className="text-slate-600">Extra employees {money(p.extra_employee_monthly)} each / month</li>
        </ul>
        <ul className="mt-4 flex-1 space-y-1.5 border-t border-slate-100 pt-4 text-sm">{p.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#00A85C]" />{FEATURE_LABELS[f] || f}</li>)}</ul>
        <div className="mt-6">{action(p, current === p.plan_id)}</div>
      </div>
    ))}
  </div>
);

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("month");
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  return (
    <section className="mx-auto max-w-6xl px-6 py-16" data-testid="pricing-page">
      <p className="text-sm font-semibold uppercase tracking-wider text-[#FF8A00]">Pricing</p>
      <h1 className="mt-2 max-w-2xl text-4xl font-bold tracking-tight text-[#0B2D5B] sm:text-5xl">Simple plans for Australian teams</h1>
      <p className="mt-4 max-w-2xl text-slate-600">14-day free trial. No lock-in contract. Prices in AUD, excluding GST. GST is added at checkout.</p>
      <div className="mt-8"><IntervalToggle value={interval} onChange={setInterval} /></div>
      <div className="mt-10"><PlanCards plans={plans} interval={interval} action={(p) => <Link to="/register"><Button className={`w-full rounded-full ${p.popular ? "bg-[#00C16B] hover:bg-[#00A85C]" : "bg-[#0B2D5B]"}`} data-testid={`pricing-cta-${p.plan_id}`}>Start 14-Day Free Trial</Button></Link>} /></div>
      <p className="mt-8 text-sm text-slate-500">Need more than 50 staff or a custom setup? <Link to="/contact" className="font-semibold text-[#00A85C]">Talk to us</Link> about an enterprise arrangement.</p>
    </section>
  );
}
